import { User } from '../types';
import { storageService } from './storageService';
import * as Sentry from '@sentry/react';
import { setSentryUser, getSentryLogger } from '../utils/sentryHelpers';
import { IAuthProvider, LoginResponse } from './auth/IAuthProvider';
import { SmartAuthProvider } from './auth/SmartAuthProvider';
import { SwsAuthProvider } from './auth/SwsAuthProvider';

/**
 * AuthService Interface
 * Defines the public API for authentication operations
 */
interface AuthService {
  login: (username: string, password: string, companyCode?: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  validateToken: (token: string) => Promise<boolean>;
  getCurrentUser: () => User | null;
}

/**
 * AuthServiceImpl
 * Main authentication service that orchestrates between SMART and SWS providers
 */
class AuthServiceImpl implements AuthService {
  private smartProvider: IAuthProvider;
  private swsProvider: IAuthProvider;

  constructor() {
    this.smartProvider = new SmartAuthProvider();
    this.swsProvider = new SwsAuthProvider();
  }

  /**
   * Authenticate user - routes to appropriate provider based on company code
   * Reason: Company code presence determines which API system to use
   */
  async login(username: string, password: string, companyCode?: string): Promise<LoginResponse> {
    const logger = getSentryLogger();

    return Sentry.startSpan(
      {
        op: "auth.login",
        name: "User Login",
      },
      async (span) => {
        // Set login context
        span?.setAttribute("auth.system", companyCode ? "sws" : "smart");
        span?.setAttribute("auth.has_company_code", !!companyCode);

        // Determine which provider to use based on company code presence
        const provider = (companyCode && companyCode.trim()) ? this.swsProvider : this.smartProvider;
        const systemName = provider.getSystemType().toUpperCase();

        logger.info(logger.fmt`Using ${systemName} API for authentication`, {
          hasCompanyCode: !!companyCode
        });

        try {
          const result = await provider.login(username, password, companyCode);

          if (result.success) {
            span?.setStatus({ code: 1 }); // OK
            logger.info(`${systemName} API login successful`);
          } else {
            span?.setStatus({ code: 2 }); // Error
            logger.warn(`${systemName} API login failed`, { error: result.error });
          }

          return result;
        } catch (error: any) {
          logger.error(`${systemName} API login exception`, { error: error.message });
          span?.setStatus({ code: 2 });

          return {
            success: false,
            error: `${systemName} authentication error: ${error.message}`
          };
        }
      }
    );
  }

  /**
   * Logout current user - routes to appropriate provider based on user's system
   */
  async logout(): Promise<void> {
    const logger = getSentryLogger();
    const user = this.getCurrentUser();
    const token = storageService.getItem<string>('authToken');

    await Sentry.startSpan(
      {
        op: "auth.logout",
        name: "User Logout",
      },
      async () => {
        // Call appropriate logout API based on user's system
        if (user && token) {
          try {
            const provider = user.system === 'smart' ? this.smartProvider : this.swsProvider;
            await provider.logout(token);
          } catch (error) {
            logger.warn("Logout API call failed, proceeding with local logout", {
              error: error instanceof Error ? error.message : String(error),
              system: user.system
            });
          }
        }

        // Clear Sentry user context
        setSentryUser(null);

        // Clear all authentication data (always do this regardless of API call success)
        storageService.removeItem('currentUser');
        storageService.removeItem('authToken');
        storageService.removeItem('smartAccessToken');
        storageService.removeItem('swsAccessToken');
        storageService.removeItem('swsAppId');
        storageService.removeItem('smartPermissions');
        storageService.removeItem('swsMenuPermissions');
      }
    );
  }

  /**
   * Validate token - routes to appropriate provider based on user's system
   */
  async validateToken(token: string): Promise<boolean> {
    try {
      const user = this.getCurrentUser();
      if (!user || !token) {
        return false;
      }

      // Test the token with the appropriate provider
      const provider = user.system === 'smart' ? this.smartProvider : this.swsProvider;
      return await provider.validateToken(token);
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  /**
   * Get currently authenticated user from storage
   */
  getCurrentUser(): User | null {
    return storageService.getItem<User>('currentUser');
  }
}

export const authService = new AuthServiceImpl();
